import React, { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { jwtDecode } from 'jwt-decode';
import Footer from "../Footer/Footer";
import { GetPamSearchCountsByDate, PamSearchData } from "../../Admin-Api/GetPamSearchCountsByDate";
import { useLocation } from "react-router-dom";


interface CustomJwtPayload {
    RoleId: string;
}

const SearchHistory: React.FC = () => {
    const navigate = useNavigate();
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [pamCode, setPamCode] = useState<string>("");

    const [pamSearchData, setPamSearchData] = useState<PamSearchData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false); // Track if search has been performed
    const restoredOnce = useRef(false);
    const location = useLocation();

    

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error("No auth token found. Please log in.");
            navigate('/');
        } else {
            try {
                const decodedToken = jwtDecode<CustomJwtPayload>(token);
                if (decodedToken.RoleId !== "1") {
                    toast.error("Unauthorized access: RoleId is not 1");
                    navigate('/');
                }
            } catch (error) {
                toast.error("Invalid token. Please log in again.");
                navigate('/');
            }
        }
    }, [navigate]);

    // Restore search state when navigating back, only once per mount
    useEffect(() => {
        const locationState = location.state as any;

        if (locationState?.restoreSearch && !restoredOnce.current) {
            const { fromDate: savedFromDate, toDate: savedToDate, pamCode: savedPamCode, pamSearchData: savedPamSearchData } = locationState;

            if (savedFromDate) {
                setFromDate(new Date(savedFromDate));
            }

            if (savedToDate) {
                setToDate(new Date(savedToDate));
            }

            if (savedPamCode) {
                setPamCode(savedPamCode);
            }

            if (savedPamSearchData && Array.isArray(savedPamSearchData)) {
                setPamSearchData(savedPamSearchData);
                setHasSearched(true);
            }

            restoredOnce.current = true;

            const newState = { ...locationState };
            delete newState.restoreSearch;
            navigate(location.pathname, { replace: true, state: newState });
        }
    }, [location.state, navigate, location.pathname]);

   

    const formatDate = (date: Date | null) => {
        if (!date) return "";
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
    };

    const formatDisplayDate = (date: Date | null) => {
        if (!date) return "";
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatDateRange = (startDate: Date | null, endDate: Date | null) => {
        if (!startDate || !endDate) return "";
        if (startDate.getTime() === endDate.getTime()) {
            return formatDisplayDate(startDate);
        }
        return `${formatDisplayDate(startDate)} to ${formatDisplayDate(endDate)}`;
    };

    const formatPamCode = (code: string) => {
        if (!code) return "";

        const clean = code.replace(/-/g, "");

        const part1 = clean.slice(0, 6);
        const part2 = clean.slice(6, 10);
        const part3 = clean.slice(10);

        let formatted = part1;

        if (part2) formatted += `-${part2}`;
        if (part3) formatted += `-${part3}`;

        return formatted;
    };

    const fetchPamSearchCounts = async (
        fromDateStr: string,
        toDateStr: string,
        pamCodeStr: string
    ) => {
        setIsLoading(true);
        setHasSearched(true);

        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No auth token found');

            const data = await GetPamSearchCountsByDate(
                fromDateStr,
                toDateStr,
                pamCodeStr, // ✅ use cleaned value
                token
            );

            setPamSearchData(data);

        } catch (error) {
            console.error('Error fetching PAM search counts:', error);
            if (!toast.isActive("fetch-error")) {
                toast.error("An error occurred while fetching search data.", { toastId: "fetch-error" });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        const fromDateStr = formatDate(fromDate);
        const toDateStr = formatDate(toDate);

        // Remove hyphens before sending
        const cleanPamCode = pamCode.replace(/-/g, "");

        if (!cleanPamCode && (!fromDate || !toDate)) {
            if (!toast.isActive("select-input")) {
                toast.info("Please select date range or enter PAM code.", { toastId: "select-input" });
            }
            return;
        }

        if (fromDate && toDate && fromDate > toDate) {
            if (!toast.isActive("invalid-range")) {
                toast.error("From date cannot be after to date.", { toastId: "invalid-range" });
            }
            return;
        }

        fetchPamSearchCounts(fromDateStr, toDateStr, cleanPamCode);
    };

    const clearFromDate = () => {
        setFromDate(null);
        setHasSearched(false);
        setPamSearchData([]);
    };

    const clearToDate = () => {
        setToDate(null);
        setHasSearched(false);
        setPamSearchData([]);
    };

    const clearDates = () => {
        setFromDate(null);
        setToDate(null);
        setHasSearched(false);
        setPamSearchData([]);
    };

    // Reset search state when dates change
    const handleFromDateChange = (date: Date | null) => {
        setFromDate(date);
        setHasSearched(false); // Reset search state
        setPamSearchData([]); // Clear previous data
    };

    const handleToDateChange = (date: Date | null) => {
        setToDate(date);
        setHasSearched(false); // Reset search state
        setPamSearchData([]); // Clear previous data
    };

    // Calculate total searches
    const totalSearches = pamSearchData.reduce((sum, response) => sum + response.searchCount, 0);

    const handlePamClick = (pamCode: string) => {
        const formatted = formatPamCode(pamCode);

        navigate(`/pamcode-details/${formatted}`, {
            state: {
                fromDate,
                toDate,
                pamCode,
                pamSearchData,
                restoreSearch: true
            }
        });
    };

    return (
    <main className="flex-1 overflow-auto px-4 sm:px-6 py-6">
        <div className="max-w-7xl mx-auto">

           

           
                <div>

                    <div className="flex flex-col sm:flex-row gap-4 mt-4 items-start">
                        <div className="w-full sm:w-auto">
                            <label className="block text-sm font-medium text-gray-700">Start Date</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={handleFromDateChange}
                                maxDate={toDate || new Date()}
                                dateFormat="dd-MM-yyyy"
                                placeholderText="dd-mm-yyyy"
                                className="border rounded p-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>

                        <div className="w-full sm:w-auto">
                            <label className="block text-sm font-medium text-gray-700">End Date</label>
                            <DatePicker
                                selected={toDate}
                                onChange={handleToDateChange}
                                minDate={fromDate ?? undefined}
                                maxDate={new Date()}
                                dateFormat="dd-MM-yyyy"
                                placeholderText="dd-mm-yyyy"
                                disabled={!fromDate}
                                className="border rounded p-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>

                        <div className="w-full sm:w-auto">
                            <label className="block text-sm font-medium text-gray-700">PAM Code</label>
                            <input
                                type="text"
                                value={pamCode}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    value = value.replace(/[^0-9-]/g, "");

                                    setPamCode(value);
                                    setHasSearched(false);
                                    setPamSearchData([]);
                                }}
                                placeholder="Search by PAM Code"
                                className="border rounded p-2 w-full focus:outline-none "
                            />
                        </div>

                        <button
                            onClick={handleSearch}
                            disabled={isLoading}
                            className="bg-indigo-600 mt-5 text-white px-4 h-10 rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 transition-colors"
                        >
                            {isLoading ? 'Fetching...' : 'Fetch Data'}
                        </button>


                    </div>

                    {isLoading && (
                        <div className="mt-4 flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
                            <p className="text-gray-500">Loading search data...</p>
                        </div>
                    )}

                    {pamSearchData.length > 0 && (
                        <div className="mt-6">
                            
                            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                                <div className="p-6">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-blue-600 rounded-md">
                                                <tr>
                                                    <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider rounded-tl-md">
                                                        S.No
                                                    </th>
                                                    <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                                                        PAM Code
                                                    </th>
                                                    <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider rounded-tr-md">
                                                        No. of Searches
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {pamSearchData
                                                    .sort((a, b) => b.searchCount - a.searchCount)
                                                    .map((response, index) => (
                                                        <tr key={response.pamCode || index}
                                                            className="hover:bg-gray-50 transition-colors"
                                                            onClick={() => handlePamClick(response.pamCode)}
                                                        >
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                                <div className="flex items-center justify-center">
                                                                    <div className="text-sm font-medium px-3 py-1">
                                                                        {formatPamCode(response.pamCode)}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                                <span className="inline-flex items-center justify-center px-3 py-1 text-sm font-medium">
                                                                    {response.searchCount}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isLoading && hasSearched && pamSearchData.length === 0 && (
                        <div className="mt-6 p-6 bg-white rounded-lg shadow-md text-center">
                            <div className="text-gray-400 mb-2">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>

                            <p className="text-gray-500 text-lg font-medium">
                                No PAM search data found
                            </p>

                            <p className="text-gray-400 text-sm">
                                {fromDate && toDate
                                    ? `for ${formatDateRange(fromDate, toDate)}`
                                    : pamCode
                                        ? `for PAM Code "${pamCode}"`
                                        : ""}
                            </p>
                        </div>
                    )}
                </div>
            
        </div>
    </main>
);
};

export default SearchHistory;
